import React, { useState } from "react";
import {
  Box,
  Typography,
  Link,
  Drawer,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { ExpandMore as ExpandMoreIcon } from "@mui/icons-material";

// FAQ data bank
const FAQ_DATA = {
  Mill: {
    header: [
      {
        question: "What is a mill?",
        answer:
          "A textile mill is an industrial facility that converts yarn or loose fibre into finished fabric through large-scale weaving, knitting and finishing processes. It sits at the fabric stage of the apparel supply chain, supplying roll goods that move downstream for garment manufacturing.",
      },
      {
        question: "What is a Scope Certificate?",
        answer:
          "A Scope Certificate is a document issued by an accredited certification body confirming that the facility meets a specific standard such as GOTS or RCS. It lists the certified sites, product categories and processing steps covered and is valid for a defined period, typically one year. The certificate proves a company is eligible to produce or trade certified goods, whereas individual shipments are covered by separate Transaction Certificates.",
      },
    ],
  },
  Weaver: {
    header: [
      {
        question: "What is a weaver or knitter?",
        answer:
          "A weaver or knitter operates looms or knitting machines to interlace or loop yarn into woven or knitted greige fabric structures. This step follows spinning and delivers untreated fabric ready for dyeing or finishing.",
      },
      {
        question: "What is a Scope Certificate?",
        answer:
          "A Scope Certificate is a document issued by an accredited certification body confirming that the facility meets a specific standard such as GOTS or RCS. It lists the certified sites, product categories and processing steps covered and is valid for a defined period, typically one year. The certificate proves a company is eligible to produce or trade certified goods, whereas individual shipments are covered by separate Transaction Certificates.",
      },
    ],
  },
  Knitter: {
    header: [
      {
        question: "What is a weaver or knitter?",
        answer:
          "A weaver or knitter operates looms or knitting machines to interlace or loop yarn into woven or knitted greige fabric structures. This step follows spinning and delivers untreated fabric ready for dyeing or finishing.",
      },
      {
        question: "What is a Scope Certificate?",
        answer:
          "A Scope Certificate is a document issued by an accredited certification body confirming that the facility meets a specific standard such as GOTS or RCS. It lists the certified sites, product categories and processing steps covered and is valid for a defined period, typically one year. The certificate proves a company is eligible to produce or trade certified goods, whereas individual shipments are covered by separate Transaction Certificates.",
      },
    ],
  },
  Dyehouse: {
    header: [
      {
        question: "What is a dyehouse?",
        answer:
          "A dyehouse is a specialized plant where greige fabric is dyed and often chemically or mechanically finished to meet colour and performance specifications. It manages colour consistency, effluent treatment and restricted-substance compliance before fabric moves further downstream.",
      },
      {
        question: "What is a Scope Certificate?",
        answer:
          "A Scope Certificate is a document issued by an accredited certification body confirming that the facility meets a specific standard such as GOTS or RCS. It lists the certified sites, product categories and processing steps covered and is valid for a defined period, typically one year. The certificate proves a company is eligible to produce or trade certified goods, whereas individual shipments are covered by separate Transaction Certificates.",
      },
    ],
  },
  Printer: {
    header: [
      {
        question: "What is a textile printer?",
        answer:
          "A textile printer applies patterns or images onto fabric using screen, roller or digital ink-jet techniques that deposit colour only where desired. This design-adding step usually comes after base dyeing and before final finishing and cutting.",
      },
      {
        question: "What is a Scope Certificate?",
        answer:
          "A Scope Certificate is a document issued by an accredited certification body confirming that the facility meets a specific standard such as GOTS or RCS. It lists the certified sites, product categories and processing steps covered and is valid for a defined period, typically one year. The certificate proves a company is eligible to produce or trade certified goods, whereas individual shipments are covered by separate Transaction Certificates.",
      },
    ],
  },
  "Yarn Importer": {
    header: [
      {
        question: "What is a yarn importer?",
        answer:
          "A yarn importer brings yarn produced abroad into the domestic market and acts as the importer-of-record responsible for customs clearance and compliance documentation. They verify origin certificates and supply certified yarn to local mills or brands.",
      },
      {
        question: "What is a Scope Certificate?",
        answer:
          "A Scope Certificate is a document issued by an accredited certification body confirming that the facility meets a specific standard such as GOTS or RCS. It lists the certified sites, product categories and processing steps covered and is valid for a defined period, typically one year. The certificate proves a company is eligible to produce or trade certified goods, whereas individual shipments are covered by separate Transaction Certificates.",
      },
    ],
  },
  "Yarn Spinner – Fiber(s)": {
    header: [
      {
        question: "What is a yarn spinner?",
        answer:
          "A spinner receives cleaned fibres and twists or air-jets them into continuous yarns while controlling count, twist and blend ratios for quality. This transformation from staple or filament to yarn links raw fibre to fabric manufacture.",
      },
      {
        question: "What is a Scope Certificate?",
        answer:
          "A Scope Certificate is a document issued by an accredited certification body confirming that the facility meets a specific standard such as GOTS or RCS. It lists the certified sites, product categories and processing steps covered and is valid for a defined period, typically one year. The certificate proves a company is eligible to produce or trade certified goods, whereas individual shipments are covered by separate Transaction Certificates.",
      },
    ],
  },
  "Fiber Trader(s)": {
    header: [
      {
        question: "What is a fiber trader?",
        answer:
          "Fiber traders buy and sell bulk natural or synthetic fibres between producers and spinners, aggregating lots to balance global supply and demand. They manage price, quality and traceability risks and may issue aggregated origin certificates when reselling fibres.",
      },
      {
        question: "What is a Scope Certificate?",
        answer:
          "A Scope Certificate is a document issued by an accredited certification body confirming that the facility meets a specific standard such as GOTS or RCS. It lists the certified sites, product categories and processing steps covered and is valid for a defined period, typically one year. The certificate proves a company is eligible to produce or trade certified goods, whereas individual shipments are covered by separate Transaction Certificates.",
      },
    ],
  },
  "Fiber Processor": {
    header: [
      {
        question: "What is a fiber processor?",
        answer:
          "A fiber processor performs preparatory treatments such as ginning cotton, scouring wool or mechanically opening recycled feedstock to clean, align and prepare fibres. The output is spinner-ready staple or tow that meets specified cleanliness and length parameters.",
      },
      {
        question: "What is a Scope Certificate?",
        answer:
          "A Scope Certificate is a document issued by an accredited certification body confirming that the facility meets a specific standard such as GOTS or RCS. It lists the certified sites, product categories and processing steps covered and is valid for a defined period, typically one year. The certificate proves a company is eligible to produce or trade certified goods, whereas individual shipments are covered by separate Transaction Certificates.",
      },
    ],
  },
  "Fiber Producer": {
    header: [
      {
        question: "What is a fiber producer?",
        answer:
          "Fiber producers are the original sources of textile raw materials, such as farms growing cotton or flax, ranches raising wool sheep, or facilities extruding synthetic or regenerated fibres. Practices at this stage establish the baseline environmental and social footprint for the entire apparel value chain.",
      },
      {
        question: "What is a Scope Certificate?",
        answer:
          "A Scope Certificate is a document issued by an accredited certification body confirming that the facility meets a specific standard such as GOTS or RCS. It lists the certified sites, product categories and processing steps covered and is valid for a defined period, typically one year. The certificate proves a company is eligible to produce or trade certified goods, whereas individual shipments are covered by separate Transaction Certificates.",
      },
    ],
  },
};

const FAQDrawer = ({ open, onClose, sectionTitle }) => {
  // Get FAQ data for the current section
  const sectionFAQs = FAQ_DATA[sectionTitle] || { header: [] };

  return (
    <Drawer
      anchor="right"
      sx={{
        width: "30%",
        "& .MuiDrawer-paper": {
          width: "30%",
          padding: "24px",
          backgroundColor: "#f8f9fa",
        },
      }}
      open={open}
      onClose={onClose}
    >
      <Box sx={{ height: "100%", overflow: "auto" }}>
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
          <Typography
            variant="h6"
            sx={{
              color: "#333333",
              fontWeight: 600,
              fontSize: "18px",
            }}
          >
            Frequently Asked Questions
          </Typography>
        </Box>

        {/* Frequently Ask Questions Section */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="subtitle1"
            sx={{
              color: "#333333",
              fontWeight: 600,
              fontSize: "16px",
              mb: 2,
            }}
          >
            {sectionTitle || ""}
          </Typography>

          {/* FAQ Items */}
          {sectionFAQs.header.map((faq, index) => (
            <Accordion
              key={index}
              sx={{
                boxShadow: "none",
                borderRadius: "8px",
                borderBottom: "1px solid #e0e0e0",
                "&:before": {
                  display: "none",
                },
                "&.Mui-expanded": {
                  margin: 0,
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  borderRadius: "8px",
                  padding: "12px",
                  "&:hover": {
                    backgroundColor: "#f0f0f0",
                  },
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: "#333333",
                    fontSize: "14px",
                    fontWeight: 500,
                  }}
                >
                  {faq.question}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ padding: "0 0 16px 0" }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#666666",
                    fontSize: "13px",
                    lineHeight: 1.5,
                    padding: "12px",
                  }}
                >
                  {faq.answer}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>

        {/* Resource Section */}
        <Box>
          <Typography
            variant="subtitle1"
            sx={{
              color: "#333333",
              fontWeight: 600,
              fontSize: "16px",
              mb: 2,
            }}
          >
            Resource
          </Typography>

          <Link
            href="/path-to-reformation-certificate-toolkit.pdf"
            target="_blank"
            sx={{
              color: "#1976d2",
              textDecoration: "underline",
              fontSize: "14px",
              fontWeight: 500,
              "&:hover": {
                color: "#1565c0",
              },
            }}
          >
            {/* Reformation Certificate Toolkit.pdf */}
          </Link>
        </Box>
      </Box>
    </Drawer>
  );
};

export default FAQDrawer;
